import React, { useEffect, useState, useCallback } from 'react';
import { X, User, Package, DollarSign, Calendar, Printer, Truck, MapPin, Mail, Phone, Copy, Check, ExternalLink, Building2, ClipboardList } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import type { ShopifyOrder } from '../../types/shopify';
import {
  formatCurrency,
  formatOrderDate,
  getFinancialStatusColor,
  getFulfillmentDisplayColor,
  getFulfillmentDisplayLabel,
} from '../../types/shopify';
import { OrderInvoice } from './OrderInvoice';
import { PickListPrintable } from './PickListPrintable';
import { checkShopifyOrderLiveStatus } from '../../utils/shopifyOrderLiveStatus';
import { getAdminId } from '../../utils/getAdminId';

interface OrderDetailsProps {
  order: ShopifyOrder;
  onClose: () => void;
  onOrderUpdated?: () => void;
}

const getCustomerInitials = (order: ShopifyOrder): string => {
  if (order.customer_first_name && order.customer_last_name) {
    return `${order.customer_first_name[0]}${order.customer_last_name[0]}`.toUpperCase();
  }
  if (order.customer_name) {
    const parts = order.customer_name.split(' ');
    if (parts.length >= 2) {
      return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    }
    return order.customer_name.substring(0, 2).toUpperCase();
  }
  return '??';
};

const formatFullAddress = (order: ShopifyOrder): string[] => {
  if (!order.shipping_address) return [];
  const lines: string[] = [];
  if (order.shipping_address.name) lines.push(order.shipping_address.name);
  if (order.shipping_address.company) lines.push(order.shipping_address.company);
  if (order.shipping_address.address1) lines.push(order.shipping_address.address1);
  if (order.shipping_address.address2) lines.push(order.shipping_address.address2);
  const cityLine = [order.shipping_address.zip, order.shipping_address.city].filter(Boolean).join(' ');
  if (cityLine) lines.push(cityLine);
  const regionLine = [order.shipping_address.province, order.shipping_address.country].filter(Boolean).join(', ');
  if (regionLine) lines.push(regionLine);
  return lines;
};

const OrderDetails: React.FC<OrderDetailsProps> = ({ order, onClose, onOrderUpdated }) => {
  const { user } = useAuth();
  const [showInvoice, setShowInvoice] = useState(false);
  const [showPickList, setShowPickList] = useState(false);
  const [pickListOrder, setPickListOrder] = useState<ShopifyOrder>(order);
  const [refreshingPickList, setRefreshingPickList] = useState(false);
  const [autoFulfillmentEnabled, setAutoFulfillmentEnabled] = useState(false);
  const [defaultCarrier, setDefaultCarrier] = useState('PostNL');
  const [copiedField, setCopiedField] = useState<string | null>(null);

  const copyToClipboard = (text: string, field: string) => {
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 2000);
  };

  const handlePrintPickList = async () => {
    if (refreshingPickList) return;
    setRefreshingPickList(true);
    try {
      const liveStatus = await checkShopifyOrderLiveStatus(order.shopify_order_id);
      const refreshedOrder: ShopifyOrder = liveStatus.lineItems?.length
        ? {
            ...order,
            line_items: liveStatus.lineItems,
            fulfillment_status: liveStatus.fulfillmentStatus ?? order.fulfillment_status,
          }
        : order;
      setPickListOrder(refreshedOrder);
    } catch {
      setPickListOrder(order);
    } finally {
      setRefreshingPickList(false);
      setShowPickList(true);
    }
  };

  const copyFullAddress = () => {
    const lines = formatFullAddress(order);
    if (lines.length > 0) {
      copyToClipboard(lines.join('\n'), 'address');
    }
  };

  const fetchFulfillmentSettings = useCallback(async () => {
    if (!user) return;

    const adminId = getAdminId(user);

    const { data } = await supabase
      .from('shopify_settings')
      .select('auto_fulfillment_enabled, default_carrier')
      .eq('admin_id', adminId)
      .maybeSingle();

    if (data) {
      setAutoFulfillmentEnabled(data.auto_fulfillment_enabled ?? false);
      setDefaultCarrier(data.default_carrier ?? 'PostNL');
    }
  }, [user]);

  useEffect(() => {
    fetchFulfillmentSettings();
  }, [fetchFulfillmentSettings]);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [onClose]);

  const handleFulfilled = () => {
    if (onOrderUpdated) {
      onOrderUpdated();
    }
    onClose();
  };

  const getStatusBadgeClass = (color: string) => {
    const colorMap: Record<string, string> = {
      green: 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 border-green-200 dark:border-green-800',
      yellow: 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300 border-yellow-200 dark:border-yellow-800',
      red: 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 border-red-200 dark:border-red-800',
      blue: 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 border-blue-200 dark:border-blue-800',
      orange: 'bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300 border-orange-200 dark:border-orange-800',
      gray: 'bg-gray-100 dark:bg-gray-900/30 text-gray-700 dark:text-gray-300 border-gray-200 dark:border-gray-800',
    };
    return colorMap[color] || colorMap.gray;
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        <div
          className="fixed inset-0 transition-opacity bg-gray-500 dark:bg-gray-900 bg-opacity-75 dark:bg-opacity-75"
          onClick={onClose}
        />

        <span className="hidden sm:inline-block sm:align-middle sm:h-screen">&#8203;</span>

        <div className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full">
          <div className="bg-white dark:bg-gray-800 px-6 py-5 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                  Order {order.order_number}
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                  <Calendar className="h-4 w-4 inline mr-1" />
                  {formatOrderDate(order.order_date)}
                </p>
              </div>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300 transition-colors"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
            <div className="flex gap-3 mt-4">
              <span
                className={`px-3 py-1 text-xs font-semibold rounded-full border ${getStatusBadgeClass(
                  getFinancialStatusColor(order.financial_status)
                )}`}
              >
                {order.financial_status.replace(/_/g, ' ')}
              </span>
              <span
                className={`px-3 py-1 text-xs font-semibold rounded-full border ${getStatusBadgeClass(
                  getFulfillmentDisplayColor(order.financial_status, order.fulfillment_status, order.cancelled_at)
                )}`}
              >
                {getFulfillmentDisplayLabel(order.financial_status, order.fulfillment_status, order.cancelled_at)}
              </span>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 px-6 py-6 max-h-[70vh] overflow-y-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
              <div className="card p-4 bg-gradient-to-br from-gray-50 to-white dark:from-gray-800 dark:to-gray-800/50 border border-gray-200 dark:border-gray-700">
                <div className="flex items-center gap-2 mb-4">
                  <User className="h-5 w-5 text-cyan-600 dark:text-cyan-400" />
                  <h4 className="font-semibold text-gray-900 dark:text-gray-100">Customer Information</h4>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center text-white text-lg font-bold shadow-lg flex-shrink-0">
                    {getCustomerInitials(order)}
                  </div>
                  <div className="flex-1 min-w-0 space-y-2">
                    {order.customer_name ? (
                      <div>
                        <p className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                          {order.customer_name}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          Shopify Order ID: {order.shopify_order_id}
                        </p>
                      </div>
                    ) : (
                      <p className="text-gray-500 dark:text-gray-400 italic">No customer name</p>
                    )}
                    {order.customer_email && (
                      <div className="flex items-center gap-2 group">
                        <Mail className="h-4 w-4 text-gray-400 flex-shrink-0" />
                        <span className="text-sm text-gray-700 dark:text-gray-300 truncate">{order.customer_email}</span>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            copyToClipboard(order.customer_email!, 'email');
                          }}
                          className="opacity-0 group-hover:opacity-100 transition-opacity p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
                        >
                          {copiedField === 'email' ? (
                            <Check className="h-3.5 w-3.5 text-green-500" />
                          ) : (
                            <Copy className="h-3.5 w-3.5 text-gray-400" />
                          )}
                        </button>
                      </div>
                    )}
                    {(order.customer_phone || order.shipping_address?.phone) && (
                      <div className="flex items-center gap-2 group">
                        <Phone className="h-4 w-4 text-gray-400 flex-shrink-0" />
                        <a
                          href={`tel:${order.customer_phone || order.shipping_address?.phone}`}
                          className="text-sm text-gray-700 dark:text-gray-300 hover:text-cyan-600 dark:hover:text-cyan-400"
                        >
                          {order.customer_phone || order.shipping_address?.phone}
                        </a>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            copyToClipboard(order.customer_phone || order.shipping_address?.phone || '', 'phone');
                          }}
                          className="opacity-0 group-hover:opacity-100 transition-opacity p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
                        >
                          {copiedField === 'phone' ? (
                            <Check className="h-3.5 w-3.5 text-green-500" />
                          ) : (
                            <Copy className="h-3.5 w-3.5 text-gray-400" />
                          )}
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {order.shipping_address && (
                <div className="card p-4 bg-gradient-to-br from-gray-50 to-white dark:from-gray-800 dark:to-gray-800/50 border border-gray-200 dark:border-gray-700">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                      <MapPin className="h-5 w-5 text-cyan-600 dark:text-cyan-400" />
                      <h4 className="font-semibold text-gray-900 dark:text-gray-100">Shipping Address</h4>
                    </div>
                    <button
                      onClick={copyFullAddress}
                      className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-gray-600 dark:text-gray-400 hover:text-cyan-600 dark:hover:text-cyan-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                    >
                      {copiedField === 'address' ? (
                        <>
                          <Check className="h-3.5 w-3.5 text-green-500" />
                          Copied
                        </>
                      ) : (
                        <>
                          <Copy className="h-3.5 w-3.5" />
                          Copy
                        </>
                      )}
                    </button>
                  </div>
                  <div className="space-y-1 text-sm">
                    {order.shipping_address.name && (
                      <p className="font-medium text-gray-900 dark:text-gray-100">{order.shipping_address.name}</p>
                    )}
                    {order.shipping_address.company && (
                      <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
                        <Building2 className="h-3.5 w-3.5 flex-shrink-0" />
                        <span>{order.shipping_address.company}</span>
                      </div>
                    )}
                    <p className="text-gray-700 dark:text-gray-300">{order.shipping_address.address1}</p>
                    {order.shipping_address.address2 && (
                      <p className="text-gray-700 dark:text-gray-300">{order.shipping_address.address2}</p>
                    )}
                    <p className="text-gray-700 dark:text-gray-300">
                      {order.shipping_address.zip} {order.shipping_address.city}
                    </p>
                    <p className="text-gray-700 dark:text-gray-300">
                      {order.shipping_address.province && `${order.shipping_address.province}, `}
                      {order.shipping_address.country}
                    </p>
                    {order.shipping_address.phone && (
                      <div className="flex items-center gap-2 mt-2 pt-2 border-t border-gray-200 dark:border-gray-700">
                        <Phone className="h-3.5 w-3.5 text-gray-400" />
                        <a
                          href={`tel:${order.shipping_address.phone}`}
                          className="text-gray-600 dark:text-gray-400 hover:text-cyan-600 dark:hover:text-cyan-400"
                        >
                          {order.shipping_address.phone}
                        </a>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            <div className="card p-4 mb-6">
              <div className="flex items-center gap-2 mb-4">
                <Package className="h-5 w-5 text-cyan-600 dark:text-cyan-400" />
                <h4 className="font-semibold text-gray-900 dark:text-gray-100">Order Items</h4>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="border-b border-gray-200 dark:border-gray-700">
                    <tr>
                      <th className="text-left py-2 px-2 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                        Product
                      </th>
                      <th className="text-left py-2 px-2 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                        SKU
                      </th>
                      <th className="text-right py-2 px-2 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                        Qty
                      </th>
                      <th className="text-right py-2 px-2 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                        Price
                      </th>
                      <th className="text-right py-2 px-2 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                        Total
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                    {order.line_items.map((item) => (
                      <tr key={item.id}>
                        <td className="py-3 px-2">
                          <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                            {item.title}
                          </p>
                        </td>
                        <td className="py-3 px-2">
                          <p className="text-sm text-gray-600 dark:text-gray-400">
                            {item.sku || 'N/A'}
                          </p>
                        </td>
                        <td className="py-3 px-2 text-right">
                          <p className="text-sm text-gray-900 dark:text-gray-100">
                            {item.quantity}
                          </p>
                        </td>
                        <td className="py-3 px-2 text-right">
                          <p className="text-sm text-gray-900 dark:text-gray-100">
                            {formatCurrency(parseFloat(item.price), item.currency)}
                          </p>
                        </td>
                        <td className="py-3 px-2 text-right">
                          <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                            {formatCurrency(parseFloat(item.price) * item.quantity, item.currency)}
                          </p>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="card p-4">
              <div className="flex items-center gap-2 mb-4">
                <DollarSign className="h-5 w-5 text-cyan-600 dark:text-cyan-400" />
                <h4 className="font-semibold text-gray-900 dark:text-gray-100">Order Summary</h4>
              </div>
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600 dark:text-gray-400">Subtotal</span>
                  <span className="text-gray-900 dark:text-gray-100 font-medium">
                    {formatCurrency(order.subtotal_price, order.currency)}
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600 dark:text-gray-400">Tax</span>
                  <span className="text-gray-900 dark:text-gray-100 font-medium">
                    {formatCurrency(order.total_tax, order.currency)}
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600 dark:text-gray-400">Shipping</span>
                  <span className="text-gray-900 dark:text-gray-100 font-medium">
                    {formatCurrency(order.total_shipping, order.currency)}
                  </span>
                </div>
                <div className="border-t border-gray-200 dark:border-gray-700 pt-2 mt-2">
                  <div className="flex justify-between">
                    <span className="text-base font-semibold text-gray-900 dark:text-gray-100">
                      Total
                    </span>
                    <span className="text-base font-bold text-gray-900 dark:text-gray-100">
                      {formatCurrency(order.total_price, order.currency)}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {order.customer_note && (
              <div className="card p-4 mt-6">
                <h4 className="font-semibold text-gray-900 dark:text-gray-100 mb-2">
                  Customer Note
                </h4>
                <p className="text-sm text-gray-600 dark:text-gray-400">{order.customer_note}</p>
              </div>
            )}

            {order.tracking_code && (
              <div className="card p-4 mt-6 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800">
                <div className="flex items-center gap-2 mb-2">
                  <Truck className="h-5 w-5 text-green-600 dark:text-green-400" />
                  <h4 className="font-semibold text-green-700 dark:text-green-300">Tracking Information</h4>
                </div>
                <div className="text-sm">
                  <p className="text-green-700 dark:text-green-300">
                    <span className="font-medium">Carrier:</span> {order.carrier || 'N/A'}
                  </p>
                  <p className="text-green-700 dark:text-green-300 font-mono">
                    <span className="font-medium font-sans">Tracking:</span> {order.tracking_code}
                  </p>
                </div>
              </div>
            )}
          </div>

          <div className="bg-gray-50 dark:bg-gray-800/50 px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex flex-col sm:flex-row gap-3 sm:justify-between">
            <button
              onClick={onClose}
              className="px-6 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg font-medium transition-colors hover:bg-gray-100 dark:hover:bg-gray-700"
            >
              Close
            </button>
            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={handlePrintPickList}
                disabled={refreshingPickList}
                className="px-6 py-2 border-2 border-teal-500 text-teal-600 dark:text-teal-400 rounded-lg font-medium transition-colors hover:bg-teal-50 dark:hover:bg-teal-900/20 flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                <ClipboardList className="h-4 w-4" />
                {refreshingPickList ? 'Refreshing...' : 'Print Pick List'}
              </button>
              <button
                onClick={() => setShowInvoice(true)}
                className="px-6 py-2 bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-700 hover:to-blue-700 text-white rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
              >
                <Printer className="h-4 w-4" />
                Print Invoice
              </button>
            </div>
          </div>
        </div>
      </div>

      {showInvoice && (
        <OrderInvoice
          order={order}
          onClose={() => setShowInvoice(false)}
          onFulfilled={handleFulfilled}
          autoFulfillmentEnabled={autoFulfillmentEnabled}
          defaultCarrier={defaultCarrier}
        />
      )}

      {showPickList && (
        <PickListPrintable
          order={pickListOrder}
          onClose={() => setShowPickList(false)}
        />
      )}
    </div>
  );
};

export default OrderDetails;
